/**
 * @file Shared native-backed field styling for form and detail surfaces.
 */

import {
  restrictCurrencyInput,
} from "../utils/currencyInput.js";

export const FIELD_WRAPPER_CLASSES = "min-w-0 space-y-1";
export const FIELD_LABEL_CLASSES = "block text-base text-gray-500 sm:text-sm";
export const REQUIRED_MARK_HTML =
  '<span class="ml-1 font-semibold text-red-600" aria-label="required">*</span>';
const NATIVE_FIELD_CLASS = "native-backed-field";
const NATIVE_INLINE_EDIT_CLASS = "native-inline-edit-field";
const READ_ONLY_VALUE_SELECTOR = "[data-native-readonly-value], .native-readonly-value";
const NEUTRAL_FOCUS_BORDER = "#e5e7eb";
const NEUTRAL_FOCUS_SHADOW = "0 0 0 1.5px rgba(71, 85, 105, 0.55)";
const INLINE_FOCUS_LINE = "2px solid rgba(71, 85, 105, 0.55)";
const REQUIRED_ERROR_SHADOW = "0 0 0 1.5px rgba(220, 38, 38, 0.22)";
let nextNativeValidationId = 1;

export const NATIVE_INPUT_CLASSES = [
  `${NATIVE_FIELD_CLASS} min-h-10 w-full rounded-md border border-gray-200 bg-white px-2 py-1`,
  "text-base text-gray-700 placeholder:text-gray-400 sm:min-h-[30px] sm:text-sm",
  "hover:border-[var(--dfc-brand-primary)] focus:hover:border-gray-200 focus:outline-none",
  "focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]",
  "dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400",
].join(" ");
export const NATIVE_INLINE_SELECT_CLASSES = [
  `${NATIVE_FIELD_CLASS} min-h-10 rounded-md border border-gray-200 bg-white px-2 py-1`,
  "text-base text-gray-700 sm:min-h-[30px] sm:text-sm",
  "hover:border-[var(--dfc-brand-primary)] focus:hover:border-gray-200 focus:outline-none",
  "focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]",
  "dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200",
].join(" ");
export const READ_ONLY_VALUE_CLASSES = [
  "flex min-h-10 items-center rounded-md bg-gray-100 px-2 py-1 text-base text-gray-700",
  "sm:min-h-[30px] sm:text-sm dark:bg-gray-700 dark:text-gray-300",
].join(" ");
export const FIXED_TEXTAREA_CLASSES = [
  `${NATIVE_FIELD_CLASS} h-24 w-full resize-y rounded-md border border-gray-200 bg-white px-2 py-1`,
  "text-base text-gray-700 placeholder:text-gray-400 sm:text-sm",
  "hover:border-[var(--dfc-brand-primary)] focus:hover:border-gray-200 focus:outline-none",
  "focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]",
  "dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400",
].join(" ");
export const AUTO_GROWING_TEXTAREA_CLASSES = [
  `${NATIVE_FIELD_CLASS} min-h-10 w-full resize-none overflow-hidden rounded-md border border-gray-200 bg-white px-2 py-1`,
  "text-base text-gray-700 placeholder:text-gray-400 sm:min-h-[30px] sm:text-sm",
  "hover:border-[var(--dfc-brand-primary)] focus:hover:border-gray-200 focus:outline-none",
  "focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]",
  "dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400",
].join(" ");
export const COMPACT_INTEGER_CLASSES = [
  `${NATIVE_FIELD_CLASS} no-spinner min-h-10 w-14 rounded-md border border-gray-200 bg-white px-2 py-1`,
  "text-base text-gray-700 placeholder:text-gray-400 sm:min-h-[30px] sm:text-sm",
  "hover:border-[var(--dfc-brand-primary)] focus:hover:border-gray-200 focus:outline-none",
  "focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]",
  "dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400",
].join(" ");
export const DETAIL_TITLE_INPUT_CLASSES = [
  `${NATIVE_FIELD_CLASS} ${NATIVE_INLINE_EDIT_CLASS} min-w-0 w-full rounded-none border-0 border-b border-gray-200 bg-transparent px-0 py-1`,
  "text-2xl font-semibold leading-tight text-gray-900 placeholder:text-gray-400 transition-colors",
  "focus:outline-none dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500",
].join(" ");
export const INLINE_EDIT_INPUT_CLASSES = [
  `${NATIVE_FIELD_CLASS} ${NATIVE_INLINE_EDIT_CLASS} min-w-0 w-full rounded-none border-0 border-b border-transparent bg-transparent px-0 py-0.5`,
  "text-sm text-gray-700 placeholder:text-gray-400 transition-colors",
  "focus:outline-none dark:text-gray-200 dark:placeholder-gray-500",
].join(" ");
export const NATIVE_CHECKBOX_CLASSES = [
  "sr-only",
].join(" ");
export const NATIVE_CHECKBOX_LABEL_CLASSES =
  "inline-flex min-h-7 cursor-pointer items-center gap-2 text-base text-gray-700 sm:min-h-5 sm:text-sm dark:text-gray-300";
export const NATIVE_CHECKBOX_MARKER_CLASSES = [
  "inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border border-gray-300 bg-white",
  "transition-colors sm:h-4 sm:w-4",
].join(" ");
export const NATIVE_CHECKBOX_ICON_CLASSES =
  "material-symbols-outlined text-[18px] leading-none sm:text-[13px]";
export const DYNAMIC_HEADING_CLASSES =
  "col-span-1 md:col-span-4 mt-1 mb-0.5 text-sm text-gray-900 dark:text-gray-100";

const SKIPPED_NATIVE_FIELD_TYPES = [
  "button",
  "checkbox",
  "file",
  "hidden",
  "radio",
  "reset",
  "submit",
];

/**
 * Renders a standard modal field label.
 * @param {string} label Label text.
 * @param {boolean} [required=false] Whether to append the required mark.
 * @returns {string} Label HTML.
 */
export function getFieldLabelHtml(label, required = false) {
  return `<label class="${FIELD_LABEL_CLASSES}">${label}${required ? REQUIRED_MARK_HTML : ""}</label>`;
}

/**
 * Renders a native-backed field wrapper with the approved label rhythm.
 * @param {string} label Field label.
 * @param {string} controlHtml Field control or read-only value HTML.
 * @param {object} [options]
 * @param {string} [options.className] Additional wrapper classes.
 * @param {boolean} [options.required=false] Whether to mark the label required.
 * @returns {string} Field wrapper HTML.
 */
export function getNativeFieldWrapperHtml(label, controlHtml, options = {}) {
  const wrapperClass = [FIELD_WRAPPER_CLASSES, options.className || ""]
    .filter(Boolean)
    .join(" ");
  return `<div class="${wrapperClass}">${getFieldLabelHtml(label, Boolean(options.required))}${controlHtml}</div>`;
}

/**
 * Renders a read-only value using the approved native-backed display recipe.
 * @param {string} value Display value.
 * @param {object} [options]
 * @param {string} [options.id] Optional element id.
 * @returns {string} Read-only value HTML.
 */
export function getReadOnlyValueHtml(value = "", options = {}) {
  const idAttr = options.id ? ` id="${options.id}"` : "";
  return `<p${idAttr} class="${READ_ONLY_VALUE_CLASSES}">${value}</p>`;
}

function getNativeFieldControls(scope) {
  return Array.from(scope.querySelectorAll(`.${NATIVE_FIELD_CLASS}`))
    .filter((control) => {
      if (!(control instanceof HTMLInputElement ||
          control instanceof HTMLSelectElement ||
          control instanceof HTMLTextAreaElement)) {
        return false;
      }
      if (control.disabled) {return false;}
      if (control instanceof HTMLInputElement &&
          SKIPPED_NATIVE_FIELD_TYPES.includes(control.type)) {
        return false;
      }
      return true;
    });
}

function getCandidateNativeFields(scope) {
  return Array.from(scope.querySelectorAll("input, select, textarea"))
    .filter((control) => {
      if (control.dataset.nativeFieldSkip === "true") {return false;}
      if (control.dataset.controlMirror) {return false;}
      if (control instanceof HTMLInputElement &&
          SKIPPED_NATIVE_FIELD_TYPES.includes(control.type)) {
        return false;
      }
      return control instanceof HTMLInputElement ||
        control instanceof HTMLSelectElement ||
        control instanceof HTMLTextAreaElement;
    });
}

function addClasses(element, className) {
  if (!element || !className) {return;}
  className.split(/\s+/).filter(Boolean).forEach((entry) => {
    element.classList.add(entry);
  });
}

function getFieldContainer(control) {
  const directContainer = control.closest(".multi-field-wrapper") ||
    control.closest("[data-native-field-wrapper]") ||
    control.parentElement;
  if (!directContainer || directContainer.tagName === "FORM") {return null;}
  return directContainer;
}

function getFieldLabel(container) {
  return container?.querySelector?.(":scope > label, :scope > span") || null;
}

function hasDirectFormControl(container) {
  return Boolean(container?.querySelector?.(":scope > input, :scope > select, :scope > textarea"));
}

function isExplicitReadOnlyValue(value) {
  return Boolean(value?.matches?.(READ_ONLY_VALUE_SELECTOR));
}

function applyReadOnlyValueTreatment(container) {
  const value = Array.from(container?.querySelectorAll?.(":scope > p") || [])
    .find((entry) => isExplicitReadOnlyValue(entry));
  if (!value) {return;}
  if (value.dataset.nativeFieldSkip === "true") {return;}
  if (value.dataset.nativeValidationMessage === "true") {return;}
  addClasses(value, READ_ONLY_VALUE_CLASSES);
}

function parseNativeCurrencyValue(control) {
  const rawValue = String(control.value || "").trim();
  if (!rawValue) {return null;}
  if (/[^0-9,$.\-\s]/.test(rawValue)) {return Number.NaN;}
  const cleanedValue = rawValue.replace(/[^0-9.-]/g, "");
  if (!/\d/.test(cleanedValue)) {return Number.NaN;}
  const numericValue = Number(cleanedValue);
  return Number.isFinite(numericValue) ? numericValue : Number.NaN;
}

function syncNativeCurrencyValidity(control) {
  if (!(control instanceof HTMLInputElement)) {return;}
  if (!isNativeCurrencyField(control)) {return;}

  const value = parseNativeCurrencyValue(control);
  const isValid = control.value.trim() === "" || Number.isFinite(value);
  control.setCustomValidity(isValid ? "" : "Enter a valid amount.");
}

function isNativeCurrencyField(control) {
  return control instanceof HTMLInputElement &&
    (control.dataset.nativeCurrency === "true" ||
      control.classList.contains("currency-input"));
}

function prepareReadOnlyValues(scope) {
  scope.querySelectorAll?.("p").forEach((value) => {
    if (value.dataset.nativeFieldSkip === "true") {return;}
    if (value.dataset.nativeValidationMessage === "true") {return;}
    const container = value.parentElement;
    const label = getFieldLabel(container);
    if (!label) {return;}
    if (!isExplicitReadOnlyValue(value) && hasDirectFormControl(container)) {return;}
    addClasses(container, FIELD_WRAPPER_CLASSES);
    addClasses(label, FIELD_LABEL_CLASSES);
    addClasses(value, READ_ONLY_VALUE_CLASSES);
  });
}

function prepareNativeFieldControl(control) {
  if (control.classList.contains(NATIVE_FIELD_CLASS)) {return;}

  if (control instanceof HTMLTextAreaElement) {
    addClasses(control, FIXED_TEXTAREA_CLASSES);
  } else if (control.classList.contains("add-multi-select")) {
    addClasses(control, NATIVE_INLINE_SELECT_CLASSES);
  } else {
    addClasses(control, NATIVE_INPUT_CLASSES);
  }
}

/**
 * Applies approved native-backed field classes to an existing rendered surface.
 * This is useful for legacy detail renderers that still emit native controls.
 * @param {Document|HTMLElement} scope Root containing native-backed fields.
 * @returns {void}
 */
export function prepareNativeFieldSurface(scope = document) {
  if (!scope) {return;}
  getCandidateNativeFields(scope).forEach((control) => {
    prepareNativeFieldControl(control);
    if (control.classList.contains(NATIVE_INLINE_EDIT_CLASS)) {return;}
    const container = getFieldContainer(control);
    addClasses(container, FIELD_WRAPPER_CLASSES);
    addClasses(getFieldLabel(container), FIELD_LABEL_CLASSES);
    applyReadOnlyValueTreatment(container);
  });
  prepareReadOnlyValues(scope);

  scope.querySelectorAll?.(READ_ONLY_VALUE_SELECTOR)
    .forEach((value) => addClasses(value, READ_ONLY_VALUE_CLASSES));
}

function getNativeValidationMessage(control) {
  if (!control.validity || control.validity.valid) {return "";}
  if (control.validity.valueMissing) {return "";}
  if (control.validity.typeMismatch && control.type === "email") {
    return "Enter a valid email address.";
  }
  if (control.validity.typeMismatch && control.type === "url") {
    return "Enter a valid URL.";
  }
  if (control.validity.tooShort && control.minLength > 0) {
    return `Use at least ${control.minLength} characters.`;
  }
  if (control.validity.tooLong && control.maxLength > 0) {
    return `Use no more than ${control.maxLength} characters.`;
  }
  if (control.validity.rangeUnderflow && control.min) {
    return `Enter a value of ${control.min} or more.`;
  }
  if (control.validity.rangeOverflow && control.max) {
    return `Enter a value of ${control.max} or less.`;
  }
  if (control.validity.stepMismatch) {
    return "Enter a valid increment.";
  }
  if (control.validity.patternMismatch) {
    return control.title || "Enter a valid value.";
  }
  if (control.validity.badInput) {
    return "Enter a valid value.";
  }
  if (control.validity.customError) {
    return control.validationMessage || "Enter a valid value.";
  }
  return control.validationMessage || "Enter a valid value.";
}

function ensureNativeValidationMessage(control) {
  if (control.dataset.nativeValidationMessageId) {
    return document.getElementById(control.dataset.nativeValidationMessageId);
  }

  const message = document.createElement("p");
  message.id = `native-field-validation-${nextNativeValidationId}`;
  nextNativeValidationId += 1;
  message.dataset.nativeFieldSkip = "true";
  message.dataset.nativeValidationMessage = "true";
  message.className = "mt-1 hidden text-xs font-medium text-red-600";
  message.setAttribute("aria-live", "polite");
  control.dataset.nativeValidationMessageId = message.id;
  control.insertAdjacentElement("afterend", message);
  return message;
}

function updateNativeFieldState(control) {
  syncNativeCurrencyValidity(control);

  const validationVisible = control.dataset.nativeFieldValidationVisible === "true";
  const isInvalid = validationVisible && !control.validity.valid;
  const isFocused = document.activeElement === control;
  const isHovered = control.dataset.nativeFieldHovered === "true";
  const messageElement = ensureNativeValidationMessage(control);
  const message = isInvalid ? getNativeValidationMessage(control) : "";

  if (control.classList.contains(NATIVE_INLINE_EDIT_CLASS)) {
    control.setAttribute("aria-invalid", String(isInvalid));
    if (message) {
      control.setAttribute("aria-describedby", messageElement.id);
    } else if (control.getAttribute("aria-describedby") === messageElement.id) {
      control.removeAttribute("aria-describedby");
    }
    messageElement.textContent = message;
    messageElement.classList.toggle("hidden", !message);

    if (isInvalid) {
      control.style.borderBottomColor = "transparent";
      control.style.boxShadow = "inset 0 -2px 0 #dc2626";
    } else if (isFocused) {
      control.style.borderBottomColor = "transparent";
      control.style.boxShadow = `inset 0 -2px 0 ${INLINE_FOCUS_LINE.replace("2px solid ", "")}`;
    } else if (isHovered) {
      control.style.borderBottomColor = "transparent";
      control.style.boxShadow = "inset 0 -1px 0 var(--dfc-brand-primary)";
    } else {
      control.style.borderBottomColor = "";
      control.style.boxShadow = "";
    }
    return;
  }

  control.classList.toggle("border-red-600", isInvalid);
  control.classList.toggle("border-gray-200", !isInvalid);
  control.setAttribute("aria-invalid", String(isInvalid));
  if (message) {
    control.setAttribute("aria-describedby", messageElement.id);
  } else if (control.getAttribute("aria-describedby") === messageElement.id) {
    control.removeAttribute("aria-describedby");
  }
  messageElement.textContent = message;
  messageElement.classList.toggle("hidden", !message);

  if (isInvalid) {
    control.style.borderColor = "#dc2626";
    control.style.boxShadow = REQUIRED_ERROR_SHADOW;
  } else {
    control.style.borderColor = isFocused ?
      NEUTRAL_FOCUS_BORDER :
      (isHovered ? "var(--dfc-brand-primary)" : "");
    control.style.boxShadow = isFocused ? NEUTRAL_FOCUS_SHADOW : "";
  }
}

function bindNativeFieldControl(control) {
  if (control.dataset.nativeFieldBehaviorBound === "true") {
    updateNativeFieldState(control);
    return;
  }

  control.dataset.nativeFieldBehaviorBound = "true";
  if (isNativeCurrencyField(control)) {
    restrictCurrencyInput(control, {
      onInput: () => {
        syncNativeCurrencyValidity(control);
        updateNativeFieldState(control);
      },
    });
  }
  if (control.required) {
    control.setAttribute("aria-required", "true");
  }

  control.addEventListener("invalid", (event) => {
    event.preventDefault();
    control.dataset.nativeFieldValidationVisible = "true";
    updateNativeFieldState(control);
  });
  control.addEventListener("focus", () => updateNativeFieldState(control));
  control.addEventListener("blur", () => updateNativeFieldState(control));
  control.addEventListener("input", () => updateNativeFieldState(control));
  control.addEventListener("change", () => updateNativeFieldState(control));
  control.addEventListener("pointerenter", () => {
    control.dataset.nativeFieldHovered = "true";
    updateNativeFieldState(control);
  });
  control.addEventListener("pointerleave", () => {
    control.dataset.nativeFieldHovered = "false";
    updateNativeFieldState(control);
  });

  updateNativeFieldState(control);
}

function updateNativeCheckboxState(input) {
  const marker = input.closest("[data-native-checkbox-label]")?.querySelector(
    "[data-native-checkbox-marker]",
  );
  const icon = marker?.querySelector("[data-native-checkbox-icon]");
  if (!marker || !icon) {return;}

  marker.style.backgroundColor = input.checked ? "var(--dfc-brand-primary)" : "";
  marker.style.borderColor = input.checked ? "var(--dfc-brand-primary)" : "#d1d5db";
  marker.style.color = input.checked ? "var(--dfc-brand-primary-contrast)" : "transparent";
  icon.classList.toggle("opacity-0", !input.checked);
}

function bindNativeCheckboxControl(input) {
  if (input.dataset.nativeCheckboxBehaviorBound === "true") {
    updateNativeCheckboxState(input);
    return;
  }

  input.dataset.nativeCheckboxBehaviorBound = "true";
  const label = input.closest("[data-native-checkbox-label]");
  const marker = label?.querySelector("[data-native-checkbox-marker]");
  marker?.setAttribute("aria-hidden", "true");

  const applyHover = () => {
    if (marker) {marker.style.borderColor = "var(--dfc-brand-primary)";}
  };
  const reset = () => updateNativeCheckboxState(input);

  input.addEventListener("change", reset);
  label?.addEventListener("pointerenter", applyHover);
  label?.addEventListener("mouseover", applyHover);
  label?.addEventListener("mouseenter", applyHover);
  label?.addEventListener("pointerleave", reset);
  label?.addEventListener("mouseleave", reset);
  updateNativeCheckboxState(input);
}

function resizeAutoGrowingTextarea(textarea) {
  textarea.style.height = "auto";
  textarea.style.height = `${textarea.scrollHeight}px`;
}

function bindAutoGrowingTextarea(textarea) {
  if (textarea.dataset.autoGrowBound === "true") {
    resizeAutoGrowingTextarea(textarea);
    return;
  }

  textarea.dataset.autoGrowBound = "true";
  textarea.addEventListener("input", () => resizeAutoGrowingTextarea(textarea));
  requestAnimationFrame(() => resizeAutoGrowingTextarea(textarea));
}

/**
 * Binds approved focus and validation behaviour to native-backed fields.
 * @param {Document|HTMLElement} scope Root containing native-backed fields.
 * @returns {void}
 */
export function bindNativeFieldBehavior(scope = document) {
  if (!scope) {return;}
  prepareNativeFieldSurface(scope);
  if (scope instanceof HTMLFormElement) {
    scope.noValidate = true;
  }
  scope.querySelectorAll?.("form").forEach((form) => {
    form.noValidate = true;
  });

  getNativeFieldControls(scope).forEach((control) => bindNativeFieldControl(control));
  scope.querySelectorAll?.('textarea[data-auto-grow="true"]')
    .forEach((textarea) => bindAutoGrowingTextarea(textarea));
  scope.querySelectorAll?.(`input[type="checkbox"].${NATIVE_CHECKBOX_CLASSES}`)
    .forEach((input) => bindNativeCheckboxControl(input));
}

/**
 * Validates native-backed fields with approved in-field error styling.
 * @param {HTMLFormElement|HTMLElement} scope Form or container to validate.
 * @returns {boolean} Whether all native-backed fields are valid.
 */
export function validateNativeFormFields(scope) {
  if (!scope) {return true;}
  bindNativeFieldBehavior(scope);

  let isValid = true;
  let firstInvalid = null;
  getNativeFieldControls(scope).forEach((control) => {
    const controlValid = control.checkValidity();
    control.dataset.nativeFieldValidationVisible = "true";
    updateNativeFieldState(control);

    if (!controlValid) {
      isValid = false;
      firstInvalid = firstInvalid || control;
    }
  });

  if (firstInvalid) {
    firstInvalid.focus({preventScroll: true});
    firstInvalid.scrollIntoView({block: "center", behavior: "smooth"});
  }

  return isValid;
}
